import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type SaleReleaseOrder,
  useDeleteSaleReleaseOrderMutation,
  useGetSaleReleaseOrdersQuery,
  useLazyGetReleaseOrderPrintPreviewQuery,
} from '../../store/api/saleControlApi'
import ReleaseOrderFormDialog from './ReleaseOrderFormDialog'
import ReleaseDetailsDialog from './ReleaseDetailsDialog'
import ReleaseOrderPrintDialog from './ReleaseOrderPrintDialog'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'
type FinalizedFilter = 'all' | 'yes' | 'no'

/** Freeze the Release No. identifier column when scrolling horizontally (skill /convert-table §7.6). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['releaseNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { releaseNo: 130 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { releaseNo: 120 }

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof SaleReleaseOrder
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + list follow Access `frmCtrlSalesQryRO` datasheet `ColumnOrder`
 * (source form_web_ui_structure_sorted.csv). release_order_scs_id (ID) is ColumnOrder #1
 * and NOT hidden → shown as first column (parity with SaleControlInvoiceView "Invoice ID").
 * No ComboBox/ListBox columns → no RowSource lookup mapping needed (convert-table §4.1).
 * Date format `d-mmm-yyyy`; numbers `Standard`/2dp right-aligned; ID = integer no separator.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'int', align: 'right' },
  { key: 'customerInvoiceNo', label: 'Customer INV' },
  { key: 'releaseNo', label: 'Release No.' },
  { key: 'releaseDate', label: 'Date', kind: 'date' },
  { key: 'sendToWarehouse', label: 'Send to WH', filter: 'checkboxes', wrap: true, width: 300 },
  { key: 'containerNo', label: 'Container No.', wrap: true, width: 520 },
  { key: 'releasableNetWeight', label: 'Releasable NW', kind: 'number', align: 'right' },
  { key: 'releaseNetWeight', label: 'Release NW', kind: 'number', align: 'right' },
  { key: 'releaseGrossWeight', label: 'Release GW', kind: 'number', align: 'right' },
  { key: 'releaseBag', label: 'Release Bag', kind: 'number', align: 'right' },
  { key: 'whRef', label: 'WH Ref', wrap: true, width: 200 },
  { key: 'contractRef', label: 'Contract Ref' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'deliveryOrder', label: 'Delivery Order' },
  { key: 'icoNo', label: 'ICO No.', wrap: true, width: 190 },
  { key: 'commodity', label: 'Commodity', filter: 'checkboxes', wrap: true, width: 520 },
  { key: 'blNumber', label: 'B/L Number', wrap: true, width: 220 },
  { key: 'blDate', label: 'B/L Date' },
  { key: 'loadingPort', label: 'Loading Port', wrap: true, width: 240 },
  { key: 'dischargePort', label: 'Discharge Port', wrap: true, width: 280 },
  { key: 'lstDeliveryTerms', label: 'Delivery Terms', filter: 'checkboxes' },
  { key: 'whRange', label: 'WH Range', wrap: true, width: 240 },
  { key: 'vesselVoyage', label: 'Vessel/Voyage', wrap: true, width: 240 },
  { key: 'invoiceQuantity', label: 'Invoice Quantity', kind: 'number', align: 'right' },
  { key: 'note', label: 'Note', wrap: true, width: 340 },
  { key: 'personUpdate', label: 'Person Updated' },
  { key: 'dateUpdate', label: 'Date Updated', kind: 'datetime' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleReleaseOrder[], key: keyof SaleReleaseOrder): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SaleControlReleaseOrderViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function SaleControlReleaseOrderView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: SaleControlReleaseOrderViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleReleaseOrder | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit'; roId: number | null } | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [triggerRoPreview] = useLazyGetReleaseOrderPrintPreviewQuery()
  const [roPrint, setRoPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false,
    title: '',
    html: '',
    loading: false,
  })

  // Access frmCtrlSalesMainSubForm*.Form_Load: Delete gate bằng SCREEN_ACCESS (canEdit), KHÔNG allowlist email.
  const [deleteSaleReleaseOrder, { isLoading: deleting }] = useDeleteSaleReleaseOrderMutation()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-release-order.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleReleaseOrder> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleReleaseOrder
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'release-order',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-release-order.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetSaleReleaseOrdersQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(shipmentPeriodStart ? { shipmentPeriodFrom: shipmentPeriodStart } : {}),
      ...(shipmentPeriodEnd ? { shipmentPeriodTo: shipmentPeriodEnd } : {}),
      finalizedFilter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleReleaseOrder) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleReleaseOrder | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  // Access frmCtrlSalesQryRO Form_Open has no GoToRecord → Access focuses the first
  // datasheet record. List is release_order_scs_id DESC, so auto-select the top row
  // once after the initial load (does not override a manual (de)selection).
  const didAutoSelect = useRef(false)
  useEffect(() => {
    if (didAutoSelect.current || isFetching) return
    if (sortedRows.length > 0 && selectedId == null) {
      setSelectedId(sortedRows[0].id)
      didAutoSelect.current = true
    }
  }, [isFetching, sortedRows, selectedId, setSelectedId])

  // ── Toolbar actions (Access frmCtrlSalesMainSubformRO, Left order) ─────────
  const requireRow = (): SaleReleaseOrder | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // btnNew / btnEdit → frmCtrlSalesQryROAdd_upgrade (ReleaseOrderFormDialog).
  // btnDetails → frmCtrlSalesQryROAdd_upgrade_detail (ReleaseDetailsDialog).
  // btnPrint → rpCtrlSalesRO (ReleaseOrderPrintDialog).
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new', roId: null })
  }
  const handleEdit = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    setFormState({ mode: 'edit', roId: row.id })
  }
  // "Detail (All)": open the datasheet of ALL details filtered by the current tab
  // filters (Shipment Period + Finalized) — no row selection needed (Access btnDetails).
  const handleDetails = () => {
    setDetailsOpen(true)
  }
  const handlePrint = async () => {
    const row = requireRow()
    if (!row) return
    if (!row.releaseNo) {
      snack.warning('Release no must be not null')
      return
    }
    setRoPrint({ open: true, title: `Print Preview - Release Order (${row.releaseNo})`, html: '', loading: true })
    try {
      const res = await triggerRoPreview(row.releaseNo).unwrap()
      setRoPrint({ open: true, title: res.title, html: res.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
      setRoPrint({ open: false, title: '', html: '', loading: false })
    }
  }

  const handleDeleteClick = () => {
    if (!requireRow()) return
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleReleaseOrder(confirmDelete.id).unwrap()
      snack.success('Successfully Deleted')
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete release order failed'))
    }
  }

  const cellValue = (row: SaleReleaseOrder, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'int' && typeof val === 'number') return String(Math.round(val))
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Button order follows Access `frmCtrlSalesMainSubformRO` (Left order):
                New · Edit · Delete · Print · Release Details. All gated by SCREEN_ACCESS. */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleNew}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={() => void handlePrint()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr-Release Order"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Pr-Release Order</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleDetails}
              startIcon={<ListAltOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Detail (All)"
            >
              Detail<Box component="span" sx={HIDE_ON_TABLET_INLINE}> (All)</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Release Orders.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Release no, invoice, container..."
              label="Search release orders"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Release Orders"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Release Order found"
              description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleReleaseOrder) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Release Order"
        message={confirmDelete ? `Do you want to delete Release Order = '${confirmDelete.releaseNo ?? ''}'?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
      {formState && (
        <ReleaseOrderFormDialog
          open
          mode={formState.mode}
          roId={formState.roId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <ReleaseDetailsDialog
        open={detailsOpen}
        shipmentPeriodFrom={shipmentPeriodStart || undefined}
        shipmentPeriodTo={shipmentPeriodEnd || undefined}
        finalizedFilter={finalizedFilter}
        onClose={() => setDetailsOpen(false)}
      />
      <ReleaseOrderPrintDialog
        open={roPrint.open}
        title={roPrint.title}
        html={roPrint.html}
        loading={roPrint.loading}
        canEdit={canEdit}
        onClose={() => setRoPrint({ open: false, title: '', html: '', loading: false })}
      />
    </Box>
  )
}
